import React, { useRef, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MdFileDownload as DownloadIcon, MdFileUpload as UploadIcon } from 'react-icons/md';

import useConfirm from './hooks/useConfirm';
import FileUploadButton from '../../shared/fileUpload/fileUploadButton.component';
import Button from '../../shared/button.component';

const ImportExportButtons = ({
  show,
  contentsForExport,
  contentsForExportLoading,
  fetchAllContentsForExport,
  resetContentsForExport,
  sendContentsForImport,
  sendingContentsForImport,
  errorNotification,
}) => {
  // export
  const downloadLinkRef = useRef(null);
  const handleExportClick = useCallback(() => {
    fetchAllContentsForExport();
  }, [fetchAllContentsForExport]);

  const [exportLink, setExportLink] = useState(null);

  useEffect(() => {
    if (contentsForExport.length && !contentsForExportLoading) {
      const blob = new Blob([JSON.stringify(contentsForExport)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      return setExportLink(url);
    }
    setExportLink(null);
  }, [contentsForExport, contentsForExportLoading]);

  useEffect(() => {
    if (exportLink) {
      downloadLinkRef.current.click();
      resetContentsForExport();
    }
  }, [exportLink, resetContentsForExport]);

  const date = new Date();
  const downloadFileName = `tlsHelpExport-${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}.json`;

  // import
  const inputRef = useRef(null);

  const [ConfirmDialog, confirmHandleFile] = useConfirm(sendContentsForImport);
  const handleFile = useCallback(event => {
    const file = event.target.files[0];

    const reader = new FileReader();
    reader.onload = () => {
      const fileContents = reader.result;
      let parsedFileContents = null;
      try {
        parsedFileContents = JSON.parse(fileContents);
      } catch (error) {
        return errorNotification('Import error. Corrupted file.');
      }

      confirmHandleFile(parsedFileContents);
    };
    reader.onabort = () => errorNotification('Import aborted. Try again.');
    reader.onerror = () => errorNotification('Import error. Try again.');

    reader.readAsText(file);
    inputRef.current.value = null; // needed to reset file input so that onChange is triggered when the same file is uploaded again
  }, [confirmHandleFile, errorNotification]);

  if (!show) {
    return null;
  }

  return (
    <div className="ImportExportButtons-container">
      <a className="ImportExportButtons-link" ref={downloadLinkRef} download={downloadFileName} href={exportLink}>Download</a>
      <Button isLoading={contentsForExportLoading} kind="transparent" onClick={handleExportClick} gray>
        <DownloadIcon className="ImportExportButtons-icon"/>
      </Button>
      <FileUploadButton
        ref={inputRef}
        accept="application/json"
        onChange={handleFile}
        className="ImportExportButtons-fileUploadButton"
        kind="transparent"
        isLoading={sendingContentsForImport}
        gray
      >
        <UploadIcon className="ImportExportButtons-icon" />
      </FileUploadButton>
      <ConfirmDialog
        className="ImportExport-confirmDialog"
        title="Are you sure you want to import help data?"
        message="Help topics that are already in the database will be overwritten!"
        dismissText="Cancel"
        submitText="Import"
      />
    </div>
  );
};

ImportExportButtons.propTypes = {
  show: PropTypes.bool.isRequired,
  contentsForExport: PropTypes.arrayOf(PropTypes.shape({
    contentId: PropTypes.number.isRequired,
    icon: PropTypes.string,
    section: PropTypes.string,
    title: PropTypes.string.isRequired,
    content: PropTypes.string,
    contentText: PropTypes.string,
    pageUrl: PropTypes.string,
    pageName: PropTypes.string,
  })).isRequired,
  contentsForExportLoading: PropTypes.bool.isRequired,
  fetchAllContentsForExport: PropTypes.func.isRequired,
  resetContentsForExport: PropTypes.func.isRequired,
  onUploadError: PropTypes.func,
  sendContentsForImport: PropTypes.func.isRequired,
  sendingContentsForImport: PropTypes.bool.isRequired,
  errorNotification: PropTypes.func.isRequired,
};

export default ImportExportButtons;
